import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";

import { db } from "../../../../../firebase";
import "../../../../../assets/styles/common/modal.scss";
import { useUsers } from "../../../../../hooks/useUsers";
import { GroupData } from "@/types/GroupData";

import ToggleButton from "../../../../button/ToggleButton";
import { ExternalLink } from "lucide-react";

const types = [
  {
    key: "quartetSemiFinal",
    label: "Quartet Semifinal",
    frontUrl: "QS",
  },
  {
    key: "quartetFinal",
    label: "Quartet Final",
    frontUrl: "QF",
  },
  {
    key: "chorusFinal",
    label: "Chorus Final",
    frontUrl: "CC",
  },
  {
    key: "evaluationOnly",
    label: "Evaluation Only",
    frontUrl: "EO",
  },
];

const randomId = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  return Array.from(
    { length: 8 },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
};

interface ResultPage {
  id: string;
  type: string;
  published: boolean;
}

interface ResultPages {
  [key: string]: ResultPage;
}

interface Props {
  open: boolean;
  mode: "create" | "manage";
  year: number;
  groups: GroupData[];
  onClose: () => void;
}

export default function ResultPageSettingModal({
  open,
  year,
  mode,
  groups,
  onClose,
}: Props) {
  const [finalistCount, setFinalistCount] = useState("");
  const [pages, setPages] = useState<ResultPages>({});
  const [loading, setLoading] = useState(false);

  const { users } = useUsers();

  const judgeUsers = users.filter((user) => user.judge);

  /**
   * 新規作成用のページを生成
   */
  const createPages = (year: number): ResultPages => {
    const result: ResultPages = {};

    types.forEach((type) => {
      result[type.key] = {
        id: `${type.frontUrl}${String(year)}${randomId()}`,
        type: type.key,
        published: false,
      };
    });

    return result;
  };

  /**
   * Quartet Contest の件数
   */
  const quartetCount = groups.filter((group) => {
    if (group.entryClass !== "QuartetContest") return false;

    const createdAt = group.createdAt?.toDate();

    return createdAt?.getFullYear() === year;
  }).length;

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setLoading(true);

      try {
        if (mode === "create") {
          setPages(createPages(year));
          setFinalistCount("");
          return;
        }

        const resultPageRef = doc(db, "resultPages", String(year));
        const snapshot = await getDoc(resultPageRef);

        if (!snapshot.exists()) {
          console.warn(`${year}年のresultPagesがFirestoreに存在しません。`);

          setPages(createPages(year));
          setFinalistCount("");
          return;
        }

        const data = snapshot.data();

        if (data.pages) {
          setPages(data.pages as ResultPages);
        } else {
          setPages(createPages(year));
        }

        if (data.finalistCount !== undefined && data.finalistCount !== null) {
          setFinalistCount(String(data.finalistCount));
        } else {
          setFinalistCount("");
        }
      } catch (error) {
        console.error("リザルトページ設定取得失敗:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [open, mode, year]);

  const handleSave = async () => {
    try {
      if (mode === "create") {
        await setDoc(doc(db, "resultPages", String(year)), {
          year: String(year),
          pages,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      if (mode === "manage") {
        const semiFinalPublished = pages.quartetSemiFinal?.published ?? false;

        const finalPublished = pages.quartetFinal?.published ?? false;

        /**
         * Semifinal / Final が公開される場合、
         * Finalist Count を必須にする
         */
        if (semiFinalPublished || finalPublished) {
          const count = Number(finalistCount);

          if (!finalistCount || count <= 0 || count > quartetCount) {
            alert(`Final出場Quartet数を1〜${quartetCount}で入力してください。`);
            return;
          }
        }

        await setDoc(
          doc(db, "resultPages", String(year)),
          {
            pages,
            finalistCount: finalistCount ? Number(finalistCount) : null,
            updatedAt: serverTimestamp(),
          },
          {
            merge: true,
          },
        );
      }

      onClose();
    } catch (error) {
      console.error("リザルトページ保存失敗:", error);
    }
  };

  const handlePublishedChange = (key: string, checked: boolean) => {
    setPages((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        published: checked,
      },
    }));
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {mode === "create" ? (
          <>
            <h2 className="modal-title">リザルト作成</h2>

            <p>以下のリザルトページを作成します。</p>
          </>
        ) : (
          <h2 className="modal-title">リザルト公開設定</h2>
        )}

        {loading ? (
          <p>読み込み中...</p>
        ) : (
          <>
            <div className="result-setting-modal">
              {mode === "manage" && (
                <div className="finalist-count-area flex-align-center">
                  <label>Final出場Quartet数</label>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={finalistCount}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (
                        value === "" ||
                        (/^\d+$/.test(value) && Number(value) <= quartetCount)
                      ) {
                        setFinalistCount(value);
                      }
                    }}
                  />

                  <span>/ {quartetCount}</span>
                </div>
              )}

              {types.map((type) => {
                const page = pages[type.key];

                const isPublished = page?.published ?? false;

                return (
                  <div key={type.key} className="setting-area">
                    <div className="flex-space-between flex-align-center">
                      <div className="flex-align-center">
                        <span className="label">
                          {type.label === "Chorus Final"
                            ? "Chorus Contest"
                            : type.label}
                        </span>

                        {mode === "manage" && page?.id && (
                          <NavLink
                            to={`/result/${page.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink size={16} />
                          </NavLink>
                        )}
                      </div>

                      {mode === "manage" && (
                        <ToggleButton
                          value={isPublished}
                          onChange={(value) =>
                            handlePublishedChange(type.key, value)
                          }
                          options={[
                            {
                              label: "公開",
                              value: true,
                              activeClass: "selected-true",
                            },
                            {
                              label: "非公開",
                              value: false,
                              activeClass: "selected-false",
                            },
                          ]}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {mode === "create" && (
              <div className="viewer-area">
                <p className="viewer-area-title">閲覧可能ユーザー</p>

                <p className="viewer-users">
                  {judgeUsers.length > 0
                    ? judgeUsers.map((user) => user.name).join("、")
                    : "なし"}
                </p>

                <p className="viewer-area-text">
                  作成前に閲覧ユーザーが正しいか必ず確認してください。
                  <br />
                  変更する場合はユーザー画面から審査権限を操作してください。
                </p>
              </div>
            )}
          </>
        )}

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>
            キャンセル
          </button>

          <button onClick={handleSave} disabled={loading}>
            {mode === "create" ? "作成する" : "保存する"}
          </button>
        </div>
      </div>
    </div>
  );
}
